/**
 * Validation Utilities
 *
 * Composable validation utilities for form validation.
 * Supports i18n by allowing custom error messages to be passed to validators.
 */

/**
 * Validator function type
 * Returns error message if invalid, undefined if valid
 */
export type Validator<T = string> = (value: T) => string | undefined;

// Default error messages (can be overridden via message parameter for i18n support)
const DEFAULT_MESSAGES = {
  required: 'This field is required',
  email: 'Please enter a valid email address',
  minLength: (min: number) => `Must be at least ${min} characters`,
  maxLength: (max: number) => `Must be no more than ${max} characters`,
  pattern: 'Invalid format',
  matches: (field: string) => `Must match ${field}`,
  numeric: 'Must be a number',
  min: (min: number) => `Must be at least ${min}`,
  max: (max: number) => `Must be no more than ${max}`,
};

// Email regex pattern (RFC 5322 simplified)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Check if value is empty (null, undefined, empty string, or whitespace only)
 */
const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

/**
 * Required field validator
 * Checks for non-empty value (not null, undefined, or empty/whitespace string)
 */
export const required = (message?: string): Validator<unknown> => {
  return (value: unknown): string | undefined => {
    if (isEmpty(value)) {
      return message || DEFAULT_MESSAGES.required;
    }
    return undefined;
  };
};

/**
 * Email format validator
 * Validates email format using RFC 5322 simplified regex
 */
export const email = (message?: string): Validator<string> => {
  return (value: string): string | undefined => {
    if (isEmpty(value)) return undefined; // Let required validator handle empty values
    if (!EMAIL_REGEX.test(value)) {
      return message || DEFAULT_MESSAGES.email;
    }
    return undefined;
  };
};

/**
 * Minimum length validator
 * Checks that string length is at least the specified minimum
 */
export const minLength = (min: number, message?: string): Validator<string> => {
  return (value: string): string | undefined => {
    if (isEmpty(value)) return undefined; // Let required validator handle empty values
    if (value.length < min) {
      return message || DEFAULT_MESSAGES.minLength(min);
    }
    return undefined;
  };
};

/**
 * Maximum length validator
 * Checks that string length does not exceed the specified maximum
 */
export const maxLength = (max: number, message?: string): Validator<string> => {
  return (value: string): string | undefined => {
    if (isEmpty(value)) return undefined; // Let required validator handle empty values
    if (value.length > max) {
      return message || DEFAULT_MESSAGES.maxLength(max);
    }
    return undefined;
  };
};

/**
 * Regex pattern validator
 * Checks that value matches the specified regular expression
 */
export const pattern = (regex: RegExp, message?: string): Validator<string> => {
  return (value: string): string | undefined => {
    if (isEmpty(value)) return undefined; // Let required validator handle empty values
    if (!regex.test(value)) {
      return message || DEFAULT_MESSAGES.pattern;
    }
    return undefined;
  };
};

/**
 * Field matching validator (for password confirmation, etc.)
 * Creates a validator that checks if value matches another field
 * Note: This returns a function that takes the other field's value
 */
export const matches = (
  fieldValue: string,
  fieldName: string,
  message?: string
): Validator<string> => {
  return (value: string): string | undefined => {
    if (isEmpty(value)) return undefined; // Let required validator handle empty values
    if (value !== fieldValue) {
      return message || DEFAULT_MESSAGES.matches(fieldName);
    }
    return undefined;
  };
};

/**
 * Numeric validator
 * Checks that value contains only numeric characters
 */
export const numeric = (message?: string): Validator<string> => {
  return (value: string): string | undefined => {
    if (isEmpty(value)) return undefined; // Let required validator handle empty values
    if (!/^\d+$/.test(value)) {
      return message || DEFAULT_MESSAGES.numeric;
    }
    return undefined;
  };
};

/**
 * Minimum numeric value validator
 * Checks that numeric value is at least the specified minimum
 */
export const min = (minValue: number, message?: string): Validator<number | string> => {
  return (value: number | string): string | undefined => {
    if (isEmpty(value)) return undefined; // Let required validator handle empty values
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue) || numValue < minValue) {
      return message || DEFAULT_MESSAGES.min(minValue);
    }
    return undefined;
  };
};

/**
 * Maximum numeric value validator
 * Checks that numeric value does not exceed the specified maximum
 */
export const max = (maxValue: number, message?: string): Validator<number | string> => {
  return (value: number | string): string | undefined => {
    if (isEmpty(value)) return undefined; // Let required validator handle empty values
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue) || numValue > maxValue) {
      return message || DEFAULT_MESSAGES.max(maxValue);
    }
    return undefined;
  };
};

/**
 * Compose multiple validators for a single value
 * Returns the first error found, or undefined if all validators pass
 */
export const validate = <T>(
  value: T,
  validators: Validator<T>[]
): string | undefined => {
  for (const validator of validators) {
    const error = validator(value);
    if (error) {
      return error;
    }
  }
  return undefined;
};

/**
 * Validate an entire form object against a schema
 * Returns an object with field names as keys and error messages as values
 */
export const validateForm = <T extends Record<string, any>>(
  values: T,
  schema: Partial<Record<keyof T, Validator<any>[]>>
): Partial<Record<keyof T, string>> => {
  const errors: Partial<Record<keyof T, string>> = {};

  for (const field of Object.keys(schema) as Array<keyof T>) {
    const validators = schema[field];
    if (validators) {
      const value = values[field];
      const error = validate(value, validators);
      if (error) {
        errors[field] = error;
      }
    }
  }

  return errors;
};

/**
 * Check if a form errors object has any errors
 * Returns true if there are any error messages, false otherwise
 */
export const hasErrors = (errors: Record<string, string | undefined>): boolean => {
  return Object.values(errors).some((error) => error !== undefined);
};

/**
 * Address completeness — mirrors the backend rule in
 * `backend/src/services/memberFieldRequirements.ts`. Keep the two in step: the
 * server rejects what this accepts, and the user only sees a toast.
 *
 * A deliverable Czech address is street + house number + city + ZIP. Either
 * house number counts (`buildingNumber` = č.p., `streetNumber` = č.o.);
 * `cityDistrict` is never required.
 */
export interface AddressParts {
  city?: string | null;
  cityDistrict?: string | null;
  street?: string | null;
  zipCode?: string | null;
  buildingNumber?: string | null;
  streetNumber?: string | null;
}

/** Address sub-fields the org requires but that are still blank. */
export const missingAddressParts = (address: AddressParts | null | undefined): string[] => {
  const filled = (v: unknown) => v !== null && v !== undefined && String(v).trim() !== '';
  if (!address) return ['street', 'buildingNumber', 'city', 'zipCode'];
  const missing: string[] = [];
  if (!filled(address.street)) missing.push('street');
  if (!filled(address.buildingNumber) && !filled(address.streetNumber)) missing.push('buildingNumber');
  if (!filled(address.city)) missing.push('city');
  if (!filled(address.zipCode)) missing.push('zipCode');
  return missing;
};

export const isAddressComplete = (address: AddressParts | null | undefined): boolean =>
  missingAddressParts(address).length === 0;
